/**
 * MONSTER TOWN — WHAT THE MONSTER IS CARRYING. FROZEN CONTRACT (src/core/*).
 * Additive only.
 *
 * Ingredients, dishes, catches and gifts all live in one flat bag keyed by a
 * content id. There is no capacity, no weight, no slot limit and no way to lose
 * anything: a kid who has picked forty apples has forty apples (LAW #2).
 *
 * ITEM IDS ARE DATA. This file has no table of items in it. `ing_apple`,
 * `dish_fruit_bowl`, `fish_pond_smiley` are strings the content crews define;
 * the bag counts them and nothing else. A new ingredient is a data drop.
 *
 * `take()` returns false when there is not enough — and that is not a failure,
 * it is the cue for a giggle and a swap (cooking brief §5). Nothing is consumed
 * on a false return; the bag is never left half-emptied.
 */

import './dayEvents'
import { bus } from './events'

export interface InventorySaveState {
  /** itemId -> count. Only positive counts are ever stored. */
  items: Record<string, number>
}

class Inventory {
  private bag = new Map<string, number>()

  count(itemId: string): number {
    return this.bag.get(itemId) ?? 0
  }

  has(itemId: string, n = 1): boolean {
    return this.count(itemId) >= n
  }

  /** Everything, as a plain object. Sorted for stable saves and stable tests. */
  all(): Record<string, number> {
    const out: Record<string, number> = {}
    for (const k of [...this.bag.keys()].sort()) {
      const v = this.bag.get(k) ?? 0
      if (v > 0) out[k] = v
    }
    return out
  }

  ids(): string[] {
    return [...this.bag.keys()].filter((k) => (this.bag.get(k) ?? 0) > 0).sort()
  }

  /** Put things in. `source` is for telemetry and for the errand engine. */
  add(itemId: string, count = 1, source = 'unknown'): number {
    if (count <= 0 || itemId === '') return this.count(itemId)
    const next = this.count(itemId) + Math.round(count)
    this.bag.set(itemId, next)
    bus.emit('inventory:gained', { itemId, count: Math.round(count), total: next, source })
    return next
  }

  /**
   * Take things out. ALL OR NOTHING: if there are not enough, nothing is taken
   * and `false` comes back. Never throws, never goes negative, never scolds.
   */
  take(itemId: string, count = 1, reason = 'used'): boolean {
    const n = Math.round(count)
    if (n <= 0) return true
    const have = this.count(itemId)
    if (have < n) return false
    const next = have - n
    if (next === 0) this.bag.delete(itemId)
    else this.bag.set(itemId, next)
    bus.emit('inventory:spent', { itemId, count: n, total: next, reason })
    return true
  }

  /** How many of a whole SET you could make. Used by recipe cards. */
  timesAvailable(need: Readonly<Record<string, number>>): number {
    let min = Infinity
    for (const [id, n] of Object.entries(need)) {
      if (n <= 0) continue
      min = Math.min(min, Math.floor(this.count(id) / n))
    }
    return Number.isFinite(min) ? min : 0
  }

  snapshot(): InventorySaveState {
    return { items: this.all() }
  }

  restore(state: InventorySaveState): void {
    this.bag.clear()
    for (const [k, v] of Object.entries(state.items ?? {})) {
      if (typeof k !== 'string' || k === '') continue
      const n = Math.round(Number(v))
      if (Number.isFinite(n) && n > 0) this.bag.set(k, n)
    }
  }

  resetForTest(): void {
    this.bag.clear()
  }
}

/** The one bag. */
export const inventory = new Inventory()
